/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import {
  Alert,
  type AutocompleteProps,
  Button,
  type ButtonProps,
  Collapse,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  type AbsoluteURL,
  type ArchivedComponentType,
  type ConfigurationWithoutRequest,
  formatDateLocal,
  objectEntries,
  type PathKeys,
} from '@poweralpha/common';
import { isEmpty, isEqual, uniqueId } from 'lodash';
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatComponentAttribute, WHITESPACE } from '../../utils';
import {
  type ConfigTypeToRequest,
  useConfigCheckArchivedComponentsQuery,
  useConfigurationQuery,
  useConfigurationsQuery,
} from '../configs/api';
import type { ConfigType } from '../configs/models';
import { ArchivedTableRow, TwoColumnInfoTable } from '../tables';
import { RenderAppLink } from '../text';
import { useDebounce } from '../useDebounce';
import FormAutocomplete from './FormAutocomplete';
import { useFieldContext } from './formContext';
import { clearFormValidationMeta } from './formValidationMeta';

const infoTableLabels = ['Version', 'Created By', 'Created At', 'Comment'];

const editBtnStyle: Record<'small' | 'tiny' | 'xs' | 'medium', SxProps<Theme>> = {
  tiny: { height: 25 },
  xs: { height: 30 },
  small: { height: 40 },
  medium: { height: 56 },
};

const editIconStyle: Record<'small' | 'tiny' | 'xs' | 'medium', SxProps<Theme>> = {
  tiny: { height: 25, width: 25 },
  xs: { height: 30, width: 30 },
  small: { height: 40, width: 40 },
  medium: { height: 56, width: 56 },
};

const archivedComponentTypeLabels: Record<ArchivedComponentType, string> = {
  batteries: 'Battery',
  essinverters: 'ESS Inverter',
  'mv-transformers': 'MV Transformer',
  pvmodules: 'PV Module',
  pvinverters: 'PV Inverter',
  weather: 'Weather',
};

// disable default autocomplete filtering, due to server side filtering
function filterOptions<T>(x: Array<T>) {
  return x;
}
interface CustomAttrsObject {
  label: string;
  render?: (val: React.ReactNode) => React.ReactNode;
}

interface Props<Type extends ConfigType> {
  label: string;
  type: Type;
  defaultProjectId?: string | null;
  editAction: (config?: ConfigurationWithoutRequest | null) => void;
  editDisabled?: boolean;
  // key can be path, e.g. 'capex.cost'
  customAttrs?: Partial<Record<PathKeys<ConfigTypeToRequest[Type]>, string | CustomAttrsObject>>;
  requestFieldName?: string;
  buttonVariant?: ButtonProps['variant'];
  size?: AutocompleteProps<unknown, boolean, boolean, boolean>['size'] | 'tiny' | 'xs';
  onRequestChange?: (request: ConfigTypeToRequest[Type]) => void;
  disabled?: boolean;
  iconOnly?: boolean;
}

function getOptionLabel(option: ConfigurationWithoutRequest | null) {
  const baseName = option ? `${option.name} v${option.version}` : '';
  if (!option?.archived) {
    return baseName;
  }
  return `${baseName} [ARCHIVED]`;
}

function isOptionEqual(option: ConfigurationWithoutRequest, value: ConfigurationWithoutRequest) {
  return option.id === value.id && option.version === value.version;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: 19/15 complexity
function FormConfigAutocompleteWithEdit<Type extends ConfigType>({
  label,
  type,
  defaultProjectId,
  editAction,
  editDisabled,
  customAttrs,
  requestFieldName,
  size,
  onRequestChange,
  buttonVariant = 'outlined',
  disabled = false,
  iconOnly = false,
}: Props<Type>) {
  const focused = useRef(false);
  const field = useFieldContext<ConfigurationWithoutRequest | null>();
  const { setFieldValue } = field.form;

  const [filterToggle, setFilterToggle] = useState(true);
  const [name, setName] = useState('');
  const [expanded, setExpanded] = useState(false);

  const selected = field.state.value;
  const configKey = field.name.split('.').at(-1) ?? '';
  const projectId = defaultProjectId ?? selected?.project_id ?? undefined;

  const debouncedName = useDebounce(name, 500);
  const configsQuery = useConfigurationsQuery(type, {
    extraQueryOptions: {
      latestVersionOnly: filterToggle,
      projectId: filterToggle ? (projectId ?? undefined) : undefined,
      showArchived: !filterToggle,
      name: debouncedName.at(0) === '=' ? undefined : debouncedName,
      id: debouncedName.at(0) === '=' ? debouncedName.slice(1) : undefined,
    },
    enabled: !disabled,
  });
  const customAttrsBool = useMemo(() => !isEmpty(customAttrs), [customAttrs]);
  const configQuery = useConfigurationQuery(type, selected?.id, {
    version: selected?.version,
    enabled: Boolean(selected) && (customAttrsBool || Boolean(requestFieldName)),
  });
  const { data: archivedComponents } = useConfigCheckArchivedComponentsQuery(type, selected?.id);

  const removeFocus = useCallback(() => {
    focused.current = false;
    setTimeout(() => {
      if (focused.current) return;
      setExpanded(false);
    }, 6e3);
  }, []);

  const onInputChange = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      setName(value);
      removeFocus();
    },
    [removeFocus],
  );

  const handleFilterClick = () => setFilterToggle((prev) => !prev);

  const infoTableValues = useMemo(
    () => [
      selected?.version ?? '1',
      selected?.created_by ?? '',
      formatDateLocal(selected?.created_at),
      <RenderAppLink
        key={`${selected?.id ?? uniqueId()}-comment`}
        text={selected?.comment ?? ''}
      />,
    ],
    [selected],
  );

  const handleEditClick = useCallback(() => {
    editAction?.(selected);
  }, [selected, editAction]);

  const refreshValidation = useEffectEvent(() => {
    clearFormValidationMeta(field.form);
    field.form.validate('change');
  });
  const getRequestFieldValue = useEffectEvent(() =>
    requestFieldName ? field.form.getFieldValue(requestFieldName) : null,
  );

  const updateForm = useEffectEvent((request: ConfigTypeToRequest[Type] | null) => {
    if (!requestFieldName) return;
    if (isEqual(getRequestFieldValue(), request)) return;
    setFieldValue(requestFieldName, request);
    refreshValidation();
  });

  useEffect(() => {
    if (!requestFieldName) return;
    if (selected && !selected.id) return;
    refreshValidation();
  }, [requestFieldName, selected]);

  useEffect(() => {
    if (requestFieldName && !selected) {
      updateForm(null);
    }
  }, [requestFieldName, selected]);

  useEffect(() => {
    if (configQuery.data && requestFieldName) {
      if (!isEqual(getRequestFieldValue(), configQuery.data.request)) {
        onRequestChange?.(configQuery.data.request);
      }
      updateForm(configQuery.data.request);
    }
  }, [configQuery.data, requestFieldName, onRequestChange]);

  return (
    <Stack>
      <Stack direction="row" spacing={0.5}>
        <Grid size="grow">
          <FormAutocomplete
            freeSolo={false}
            label={`${label} Configuration`}
            options={configsQuery.data?.results ?? []}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqual}
            onInputChange={onInputChange}
            disabled={disabled}
            filterOptions={filterOptions}
            onFocus={() => {
              focused.current = true;
              setExpanded(true);
            }}
            onBlur={removeFocus}
            size={size}
          />
        </Grid>
        <Grid>
          <Tooltip title={filterToggle ? 'Show all' : 'Filter'}>
            <span>
              <ToggleButton
                value="filter"
                size="small"
                selected={filterToggle}
                onChange={handleFilterClick}
                disabled={disabled}
                sx={editIconStyle[size ?? 'small']}
              >
                <FilterAltIcon />
              </ToggleButton>
            </span>
          </Tooltip>
        </Grid>
        <Grid>
          {iconOnly ? (
            <IconButton
              onClick={handleEditClick}
              color={selected ? 'primary' : 'success'}
              disabled={editDisabled || disabled}
              data-testid={`edit-config-${configKey}`}
              sx={editIconStyle[size ?? 'small']}
            >
              {selected ? <EditIcon /> : <AddCircleIcon />}
            </IconButton>
          ) : (
            <Button
              variant={buttonVariant}
              onClick={handleEditClick}
              color={selected ? 'primary' : 'success'}
              startIcon={selected ? <EditIcon /> : <AddCircleIcon />}
              disabled={editDisabled || disabled}
              sx={{ ...(editBtnStyle[size ?? 'small'] as object), width: 70 }}
              data-testid={`edit-config-${configKey}`}
            >
              {selected ? 'Edit' : 'Add'}
            </Button>
          )}
        </Grid>
      </Stack>
      <Grid
        size={12}
        hidden={
          (
            archivedComponents?.find((component) => component.configId === selected?.id)
              ?.archived ?? []
          )?.length === 0
        }
      >
        <Alert
          severity="warning"
          sx={{ fontSize: 12, py: 0, px: 1 }}
          slotProps={{
            message: { sx: { py: 0.5 } },
            icon: { sx: { m: 0, py: 0.8, px: 0.5, fontSize: 12 } },
          }}
        >
          This configuration has archived components.
          <List sx={{ pl: 2, py: 0 }} dense>
            {archivedComponents
              ?.find((component) => component.configId === selected?.id)
              ?.archived.map((archivedComponent) => (
                <ListItem key={archivedComponent.id} sx={{ p: 0, m: 0 }}>
                  <ListItemText slotProps={{ primary: { sx: { fontSize: 12 } } }} sx={{ my: 0 }}>
                    {archivedComponentTypeLabels[archivedComponent.type]} - {WHITESPACE}
                    <Link
                      to={
                        `/db/${archivedComponent.type}/${archivedComponent.id}/` satisfies AbsoluteURL
                      }
                      target="_blank"
                    >
                      {archivedComponent.name}
                    </Link>
                  </ListItemText>
                </ListItem>
              ))}
          </List>
        </Alert>
      </Grid>
      <Collapse in={selected ? expanded : false}>
        <TableContainer>
          <Table size="small">
            <TableBody>
              <ArchivedTableRow archived={selected?.archived} />
              {customAttrs &&
                objectEntries(customAttrs).map(([key, attr]) => (
                  <TableRow key={key as string}>
                    <TableCell>
                      {typeof attr === 'string' ? attr : (attr as CustomAttrsObject).label}
                    </TableCell>
                    <TableCell align="right">
                      {formatComponentAttribute(
                        configQuery.data?.request,
                        key as PathKeys<ConfigTypeToRequest[Type]>,
                        {
                          arrayJoinDelimiter: '+',
                          render: (attr as CustomAttrsObject).render,
                        },
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              <TwoColumnInfoTable values={infoTableValues} labels={infoTableLabels} />
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Stack>
  );
}

export default FormConfigAutocompleteWithEdit;
